// Hand — dorsal (back-of-hand) detail panel for pain capture
// (PAIN_TRACKING_PLAN.md Task 3.1). React-free / JSX-free (hazard H7):
// scripts/validatePainMap.ts and scripts/renderBodyMap.ts import this module
// directly from the repo root. JSX starts in PainMapCanvas.tsx.
//
// Own canvas (D-PAIN-6, "Canvas conventions"): 0 0 200 260, centerline x = 100.
// ONE left hand is drawn; PainMapCanvas mirrors it to the right hand at render
// time via mirrorPath. Every region is a single "-l" region.
//
// This is a LEFT hand seen from the BACK, so the thumb is on the viewer's LEFT
// (low x) — the mirror image of the palmar panel. Fingers point up (fingertips
// near y = 20, MCP row near y = 146), hand y 140-232, wrist y 230-256.
//
// Coverage (Task 3.1, dorsal aspect): MCP / PIP / DIP joint blocks per digit,
// wrist joint, scaphoid (thumb-side wrist hollow), radial nerve (first web
// space, thumb-index), ulnar nerve (dorsal ulnar border), and extensor tendon
// bands fanning from the wrist to the fingers.

import type { PainStructure } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { PainRegion } from "./painRegions.js";
import { ellipsePath } from "./painRegions.js";

export const HAND_DORSAL_VIEWBOX = "0 0 200 260";

/** A closed rounded-rectangle outline path (hand / wrist / thumb blocks). */
function box(x0: number, y0: number, x1: number, y1: number, r: number): string {
  return [
    `M ${x0 + r} ${y0}`,
    `L ${x1 - r} ${y0}`,
    `Q ${x1} ${y0} ${x1} ${y0 + r}`,
    `L ${x1} ${y1 - r}`,
    `Q ${x1} ${y1} ${x1 - r} ${y1}`,
    `L ${x0 + r} ${y1}`,
    `Q ${x0} ${y1} ${x0} ${y1 - r}`,
    `L ${x0} ${y0 + r}`,
    `Q ${x0} ${y0} ${x0 + r} ${y0}`,
    "Z",
  ].join(" ");
}

/** A rounded finger capsule from its tip (yTop) down to the MCP row (y = 150). */
function finger(cx: number, yTop: number): string {
  const yBase = 150;
  const w = 6;
  return [
    `M ${cx - w} ${yBase}`,
    `L ${cx - w} ${yTop + 6}`,
    `Q ${cx - w} ${yTop} ${cx} ${yTop}`,
    `Q ${cx + w} ${yTop} ${cx + w} ${yTop + 6}`,
    `L ${cx + w} ${yBase}`,
    "Z",
  ].join(" ");
}

export const HAND_DORSAL_OUTLINE: string[] = [
  box(68, 140, 134, 232, 14), // back of hand
  box(74, 230, 126, 256, 10), // wrist
  box(44, 152, 70, 214, 12), // thumb (viewer's left on a dorsal left hand)
  finger(78, 32), // index (d2)
  finger(94, 20), // middle (d3)
  finger(110, 26), // ring (d4)
  finger(126, 40), // little (d5)
];

/** One left-hand-only region. `kind` is read from PAIN_STRUCTURE_META so a
 *  region can never disagree with the taxonomy (validator rule S7). */
function reg(id: string, structure: PainStructure, d: string): PainRegion {
  return { id, structure, kind: PAIN_STRUCTURE_META[structure].kind, side: "left", d };
}

// Finger centrelines (thumb on the viewer's left -> little finger on the right).
const D2 = 78; // index
const D3 = 94; // middle
const D4 = 110; // ring
const D5 = 126; // little
const THUMB = 52;

export const HAND_DORSAL_STRUCTURES: PainRegion[] = [
  // ── MCP row (base of each digit) ──────────────────────────────────────────
  reg("joint-mcp-d1-l", "joint-mcp", ellipsePath(THUMB, 200, 5.5, 5)),
  reg("joint-mcp-d2-l", "joint-mcp", ellipsePath(D2, 146, 5, 4.5)),
  reg("joint-mcp-d3-l", "joint-mcp", ellipsePath(D3, 146, 5, 4.5)),
  reg("joint-mcp-d4-l", "joint-mcp", ellipsePath(D4, 146, 5, 4.5)),
  reg("joint-mcp-d5-l", "joint-mcp", ellipsePath(D5, 146, 5, 4.5)),

  // ── PIP row (middle joint; the thumb has no PIP) ──────────────────────────
  reg("joint-pip-d2-l", "joint-pip", ellipsePath(D2, 100, 5, 4.5)),
  reg("joint-pip-d3-l", "joint-pip", ellipsePath(D3, 100, 5, 4.5)),
  reg("joint-pip-d4-l", "joint-pip", ellipsePath(D4, 100, 5, 4.5)),
  reg("joint-pip-d5-l", "joint-pip", ellipsePath(D5, 100, 5, 4.5)),

  // ── DIP row (fingertip joint; d1 = the thumb IP joint) ────────────────────
  reg("joint-dip-d1-l", "joint-dip", ellipsePath(THUMB, 156, 5, 4.5)),
  reg("joint-dip-d2-l", "joint-dip", ellipsePath(D2, 56, 4.5, 4)),
  reg("joint-dip-d3-l", "joint-dip", ellipsePath(D3, 52, 4.5, 4)),
  reg("joint-dip-d4-l", "joint-dip", ellipsePath(D4, 56, 4.5, 4)),
  reg("joint-dip-d5-l", "joint-dip", ellipsePath(D5, 64, 4.5, 4)),

  // ── wrist ────────────────────────────────────────────────────────────────
  reg("joint-wrist-l", "joint-wrist", ellipsePath(100, 240, 20, 8)),
  reg("bone-scaphoid-l", "bone-scaphoid", ellipsePath(74, 228, 6, 6)),

  // ── nerves ───────────────────────────────────────────────────────────────
  // Radial: the dorsal first web space between the thumb and index finger.
  reg("nerve-radial-l", "nerve-radial", ellipsePath(66, 168, 5, 16)),
  // Ulnar: the dorsal ulnar (little-finger) border.
  reg("nerve-ulnar-l", "nerve-ulnar", ellipsePath(128, 196, 5, 40)),

  // ── soft tissue (extensor tendon bands over the back of the hand) ─────────
  reg("tissue-tendon-ext1-l", "tissue-tendon", ellipsePath(92, 180, 7, 34)),
  reg("tissue-tendon-ext2-l", "tissue-tendon", ellipsePath(110, 184, 6, 30)),
];
